import type { Guide } from "./types.js";
import { Lead, H2, P, Ul, Li, AppLink, C, Code, Callout } from "./primitives.js";

export const meta = {
  id: "dev-conventions",
  title: "Codebase conventions + Contributing",
  category: "For developers",
  lastUpdated: "2026-05-24",
  description: "TypeScript / ESM / Zod / Hebrew RTL / brand voice / migration patterns. Read before opening your first PR.",
  relatedGuides: ["dev-architecture", "dev-add-channel", "dev-add-integration"],
  readTimeMin: 7,
} as const;

export function Body() {
  return (
    <>
      <Lead>
        Conventions worth knowing before contributing. Most are enforced by TypeScript + the directory structure; some are tribal knowledge we've collected from incidents. Read once, refer back as needed.
      </Lead>

      <H2>TypeScript + module system</H2>
      <Ul>
        <Li><strong>ESM everywhere</strong> — <C>"type": "module"</C> on every package.json. Use <C>import</C> / <C>export</C>. No CommonJS.</Li>
        <Li><strong>Always include <C>.js</C> extension in imports</strong>, even from .ts/.tsx files: <C>import &#123; foo &#125; from "./bar.js"</C>. This is a Node ESM requirement.</Li>
        <Li><strong>Workspaces</strong> — root <C>package.json</C> declares <C>keywords/</C> + <C>apps/ui/</C> + <C>apps/ui-server/</C> + <C>mcps/*</C>. Cross-workspace imports use the <C>@alternabe/keywords</C> package name (symlinked into <C>node_modules</C>).</Li>
        <Li><strong>Strict mode on</strong>. Don't disable. Don't <C>any</C> your way out. If a third-party type is messy, prefer narrow casts with comments.</Li>
      </Ul>

      <H2>Zod schemas as source of truth</H2>
      <P>
        Every value that flows through a runtime boundary (DB row, API body, LLM response) gets a Zod schema in <C>keywords/src/types.ts</C>. Routes use <C>.safeParse()</C>; repositories use <C>.parse()</C> after reading from SQLite.
      </P>
      <Code>{`// keywords/src/types.ts
export const KeywordSuggestion = z.object({
  id: z.string().min(1),
  clusterId: z.string().min(1),
  // ...
});
export type KeywordSuggestion = z.infer<typeof KeywordSuggestion>;
export const KeywordSuggestionInput = KeywordSuggestion.partial().required({
  clusterId: true, keyword: true, language: true, source: true,
});
export type KeywordSuggestionInput = z.infer<typeof KeywordSuggestionInput>;`}</Code>
      <P>Pattern: <C>FooRecord</C> for the full shape, <C>FooInput</C> for the create/upsert shape, <C>FooPatch</C> for partial updates. All inferred from a single Zod object.</P>

      <H2>SQLite + migrations</H2>
      <Ul>
        <Li><strong>Better-sqlite3, sync</strong> — no Promises, no callbacks. Simple, fast, easy to back up (single file).</Li>
        <Li><strong>One singleton connection</strong> shared across repos. Defined in <C>keywords/src/db.ts</C> + exposed via <C>apps/ui-server/src/lib/repos.ts</C>.</Li>
        <Li><strong>Migrations live in <C>keywords/src/migrations/*.sql</C></strong>, applied alphabetically. The runner is idempotent — safe to call on every connection. Migration numbers are zero-padded (<C>001_</C>, <C>002_</C>, ..., <C>015_</C>) — the next one is <C>016_</C>.</Li>
        <Li><strong>Never edit an existing migration</strong> after it's been applied somewhere. Add a new one.</Li>
        <Li><strong>Always include the rationale</strong> in the migration's comment block — future-you will thank present-you.</Li>
      </Ul>

      <H2>Hebrew RTL + multilingual content</H2>
      <Ul>
        <Li><strong><C>dir="auto"</C></strong> on every user-content element (table cells, list items, free-text fields, code blocks containing Hebrew). Lets the browser pick LTR or RTL per text.</Li>
        <Li><strong>NEVER hard-code <C>dir="rtl"</C></strong> — mixed Hebrew+English breaks unpredictably.</Li>
        <Li><strong>Caption length</strong>: JS <C>String.length</C> counts UTF-16 code units. Hebrew (BMP) chars count as 1. Emoji (supplementary plane) count as 2 — slicing mid-emoji leaves dangling surrogates.</Li>
        <Li><strong>Sentence-end punctuation</strong>: <C>.!?</C> works for English. For Hebrew, also handle <C>׃</C> (Hebrew sof pasuq). The X orchestrator's splitter uses <C>(?&lt;=[.!?׃])\s+</C>.</Li>
      </Ul>

      <H2>Brand voice (must inject into every production prompt)</H2>
      <P>Defined in <C>keywords/src/brief/defaults.ts</C> as <C>BRAND_VOICE_PROMPT_BLOCK</C>. Cached as a system-prompt block on every Claude call. Three sections:</P>
      <P><strong>1. Safety rails</strong>:</P>
      <Ul>
        <Li>Friendly, informative, professional, trustworthy, practical, down-to-earth.</Li>
        <Li>Hebrew-first. Plain language, not clinical, not overly spiritual.</Li>
        <Li>NEVER promise medical outcomes, cures, or guaranteed results.</Li>
        <Li>NEVER claim therapist verification unless the verification process is explicitly described.</Li>
        <Li>AlternaBe is a marketplace and intermediary, not a medical provider.</Li>
        <Li>Always include a health disclaimer on health content.</Li>
        <Li>Treatment claims must use hedged language ("may help", "traditionally used for"). Never "cures", "treats", "proven to eliminate".</Li>
      </Ul>
      <P><strong>2. Platform tie-ins</strong> — surface relevant AlternaBe therapist categories / treatment types / store products where natural in the prose.</P>
      <P><strong>3. Humanization rules (added 2026-05-24)</strong> to evade AI-content detection:</P>
      <Ul>
        <Li><strong>NEVER use em dashes (— or –) in the article body.</strong> Single biggest AI-detection signal. Use commas, periods, parentheses, or restructure. Hyphens within compound terms (<C>ביו-רזוננס</C>) are fine.</Li>
        <Li><strong>Banned phrases</strong>: Hebrew (<C>חשוב לציין</C>, <C>נצלול לעומק</C>, <C>במאמר זה נחקור</C>, <C>מארג מורכב של</C>, <C>ניווט בעולם של</C>) + English (<C>delve into</C>, <C>navigate the complexities of</C>, <C>in today's fast-paced world</C>, <C>tapestry</C>, <C>landscape</C>, <C>embark on a journey</C>).</Li>
        <Li><strong>Vary sentence rhythm</strong> — mix short punchy with longer. Fragments OK.</Li>
        <Li><strong>No textbook section structure</strong> — don't open H2s with a one-sentence preview, don't close with a wrap-up, don't start every section with a generic transition.</Li>
        <Li><strong>Specific over generic</strong> — actual brands, actual Israeli cities, actual numbers. "Many people report..." is hollow.</Li>
        <Li><strong>Address the reader directly</strong> when natural. Show some opinion / stance.</Li>
      </Ul>
      <P>These rules apply to the article BODY (the Claude output). They explicitly do NOT apply to the prompts themselves — the block says so to avoid confusing the model.</P>

      <H2>Cost discipline</H2>
      <Ul>
        <Li><strong>Pick the cheapest model that works</strong>: Haiku 4.5 for classification / extraction / boilerplate, Sonnet 4.6 for synthesis / creative, Opus 4.6/4.7 for pillar pages only.</Li>
        <Li><strong>Cache aggressively</strong> on production prompts. The production engine's 2-block <C>cache_control</C> verifiably reads ~21K cached tokens per article — material cost savings.</Li>
        <Li><strong>Distribution captions are heuristic</strong> — never call an LLM at distribute time. See <AppLink to="help?guide=cost-optimization">Cost optimization</AppLink>.</Li>
        <Li><strong>Batch wherever possible</strong>: CSV header mapping, cannibalization grouping, classifier-over-suggestions are all batched into one Haiku call per N items.</Li>
      </Ul>

      <H2>Error handling</H2>
      <Ul>
        <Li><strong>Orchestrators NEVER throw to route handlers</strong>. Return errors via <C>result.error</C>. Routes log + respond gracefully.</Li>
        <Li><strong>Routes return 400 / 404 / 409 / 500 with <C>&#123; ok: false, error: ".." &#125;</C></strong>. UI catches and shows operator-readable error text.</Li>
        <Li><strong>Per-step success/failure</strong>: when an orchestrator does N sub-operations (send to N WhatsApp recipients, post N tweets to a thread), surface a per-step results array so partial success is visible.</Li>
        <Li><strong>Distribution logging</strong>: every distribute attempt — success or failure — gets a row in the <C>distributions</C> table. Operator can audit via the Distribution page (when it ships).</Li>
      </Ul>

      <H2>The "Why" trail</H2>
      <P>
        Major design decisions are documented in <C>PROJECT_STATE.md</C> under "Key decisions &amp; rationale". Read those before relitigating something (e.g. "why distribute-time captions are heuristic", "why mirror Sanity locally instead of live GROQ", "why cluster expansion = DFS + LLM + classifier stacked").
      </P>
      <P>
        Per-feature in-code rationale lives in:
      </P>
      <Ul>
        <Li>Migration <C>*.sql</C> file header comments — the "why" of each schema change.</Li>
        <Li>Top-of-file doc comments in orchestrators — the architectural rationale.</Li>
        <Li>Setup guides on Credentials cards — operator-facing "why this matters" for external setup.</Li>
      </Ul>

      <H2>Testing</H2>
      <Ul>
        <Li><strong>Smoke tests under <C>scripts/</C></strong>: typescript-by-convention <C>.mjs</C> files that exercise route layers against the live ui-server. See <C>test-cluster-expansion.mjs</C>, <C>test-youtube-distribute.mjs</C>, <C>test-csv-import.mjs</C>.</Li>
        <Li><strong>Dry-run mode</strong> in every distribution orchestrator lets you validate the payload without external side effects. Use as the first test for any new channel.</Li>
        <Li><strong>No unit-test framework</strong> yet — the codebase is integration-test heavy. When adding non-trivial pure logic (e.g. URL builder, hashtag extractor), inline a quick smoke at the bottom of the file or a new <C>scripts/</C> entry.</Li>
      </Ul>

      <H2>Dev workflow</H2>
      <Code>{`# Boot the full stack (Vite + Fastify in one terminal)
npm run ui:all-dev

# Or boot them separately
npm run ui:dev          # http://localhost:5173
npm run ui-server:dev   # http://localhost:4400

# Build all workspaces
npm run keywords:build
npm run serpapi:build
npm run dataforseo:build

# Run a smoke test
node scripts/test-cluster-expansion-v2.mjs`}</Code>

      <H2>Help Center maintenance</H2>
      <Callout kind="warn" title="When you ship an operator-facing feature">
        Update or add a guide in <C>apps/ui/src/pages/help/guides/</C> in the SAME session. Bump <C>meta.lastUpdated</C> to today's date. The Help Center is the operator's reference between sessions — if it's stale, the chat-panel "ask AlternaBe AI" feature gives wrong answers.
      </Callout>

      <H2>Where to learn the codebase</H2>
      <Ul>
        <Li>Read <AppLink to="help?guide=dev-architecture">Architecture overview</AppLink>.</Li>
        <Li>Open <C>keywords/src/types.ts</C> — every shape in the system, end-to-end.</Li>
        <Li>Open <C>apps/ui-server/src/routes/pipeline-actions.ts</C> — the route layer that wires UI buttons to the orchestrators.</Li>
        <Li>Trace one workflow end-to-end (e.g. produce → published) by following the imports.</Li>
        <Li>Skim <C>PROJECT_STATE.md</C> "Key decisions" section to understand WHY things are shaped the way they are.</Li>
      </Ul>
    </>
  );
}

export const guide: Guide = { meta, Body };
